import * as Sentry from '@sentry/browser';
import type { LastLocationAPI } from './definitions';
import moment from 'moment';

import { DatadogTrack } from '@cobliteam/datadog-rum';

const MIN_EVENTS_TO_START_METRIC_DELAY = 2;

export const sendLatencyMetrics = (lastTimestamp: moment.Moment | null) => {
  try {
    const timestamp = moment();

    // does not send metric in first load
    if (!lastTimestamp) {
      return timestamp;
    }

    const metric = {
      timestamp: timestamp.format(),
      lastTimestamp: lastTimestamp?.format(),
      diffTimestampAndLastTimestampInMs:
        lastTimestamp && timestamp.diff(lastTimestamp, 'ms', true),
    };

    DatadogTrack.action('Fleet-events event latency', metric);
    return timestamp;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('EventSource', 'RealtimeLatency');
      Sentry.captureException(error);
    });
    return null;
  }
};

const sumValues = (acc: number, value: number) => {
  return acc + value;
};

export const sendRealtimeDelay = (
  counterEventsReceived: number,
  translatedEventData: LastLocationAPI[]
) => {
  try {
    // does not send metric in first load
    if (
      counterEventsReceived > MIN_EVENTS_TO_START_METRIC_DELAY &&
      translatedEventData.length
    ) {
      const timestamp = moment();

      const diffEventTimeAndViewTimeInMs: number[] = [];
      const diffEventTimeAndIngestedTimeInMs: number[] = [];
      const diffIngestedTimeAndProcessedTimeInMs: number[] = [];
      const diffProcessedTimeAndLatencyTimeInMs: number[] = [];
      const diffIngestedTimeAndViewTimeInMs: number[] = [];

      translatedEventData.forEach(
        ({ time_in_millis, cobli_ingested_at, processed_at }) => {
          const timeInMillis = moment(time_in_millis);
          const cobliIngestedAt = moment(cobli_ingested_at);
          const processedAt = moment(processed_at);

          diffEventTimeAndViewTimeInMs.push(
            timestamp.diff(timeInMillis, 'ms', true)
          );

          if (cobli_ingested_at) {
            diffEventTimeAndIngestedTimeInMs.push(
              cobliIngestedAt.diff(timeInMillis, 'ms', true)
            );

            diffIngestedTimeAndViewTimeInMs.push(
              timestamp.diff(cobliIngestedAt, 'ms', true)
            );
          }

          if (processed_at && cobli_ingested_at) {
            diffIngestedTimeAndProcessedTimeInMs.push(
              processedAt.diff(cobliIngestedAt, 'ms', true)
            );
          }

          if (processed_at) {
            diffProcessedTimeAndLatencyTimeInMs.push(
              timestamp.diff(processedAt, 'ms', true)
            );
          }
        }
      );

      const metric = {
        // device until gateway
        diffEventTimeAndIngestedTimeInMsMean:
          diffEventTimeAndIngestedTimeInMs.reduce(sumValues, 0) /
          diffEventTimeAndIngestedTimeInMs.length,

        // gateway until realtime location
        diffIngestedTimeAndProcessedTimeInMsMean:
          diffIngestedTimeAndProcessedTimeInMs.reduce(sumValues, 0) /
          diffIngestedTimeAndProcessedTimeInMs.length,

        // realtime location until frontend
        diffProcessedTimeAndLatencyTimeInMsMean:
          diffProcessedTimeAndLatencyTimeInMs.reduce(sumValues, 0) /
          diffProcessedTimeAndLatencyTimeInMs.length,

        // total time
        diffEventTimeAndViewTimeInMsMean:
          diffEventTimeAndViewTimeInMs.reduce(sumValues, 0) /
          diffEventTimeAndViewTimeInMs.length,

        // gateway until frontend
        diffIngestedTimeAndViewTimeInMsMean:
          diffIngestedTimeAndViewTimeInMs.reduce(sumValues, 0) /
          diffIngestedTimeAndViewTimeInMs.length,
      };

      DatadogTrack.action('Fleet-events event delay', metric);
    }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('EventSource', 'RealtimeDelay');
      Sentry.captureException(error);
    });
  }
};
